import AppBar from "@material-ui/core/AppBar";
import Drawer from "@material-ui/core/Drawer";
import Grow from "@material-ui/core/Grow";
import IconButton from "@material-ui/core/IconButton";
import { createStyles, Theme, WithStyles, withStyles, WithTheme } from "@material-ui/core/styles";
import { duration as transitionDuration } from "@material-ui/core/styles/transitions";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import MenuIcon from "@material-ui/icons/Menu";
import classNames from "classnames";
import { Location } from "history";
import { SnackbarProvider } from "notistack";
import { SigninResponse, SignoutResponse } from "oidc-client";
import React from "react";
import { connect } from "react-redux";
import { Link, Route, RouteComponentProps, Switch } from "react-router-dom";
import { bindActionCreators } from "redux";
import {
  getUserFullName,
  getUserManager,
  isUserAuthorized,
  setUserSignedIn,
  setUserSignedOut,
  UserSignInResultDto,
} from "../../../auth";
import { getClientConfiguration } from "../../../configuration";
import { NotAuthorizedPage, NotFoundPage, SignedOutPage } from "../../../pages";
import { AppActions, AppState, baseActionCreators, BaseApplicationState, OidcState } from "../../../store";
import { isClientMobile } from "../../../utils";
import { SignInCallbackHandler, SignInHandler, SignOutCallbackHandler } from "../../auth";
import { DrawerMenu, DrawerMenuOptions, UserMenu, UserMenuOptions } from "../../navigation";
import { SignInRoute } from "../../routes";
import { SnackbarMessage, snackbarNotificationIconVariant, SnackbarNotifications } from "../../snackbars";
import { layoutContentMarginTop } from "../../style";
import { AppBarMenuOptions } from "./models";

const navDrawerConfig = {
  width: 240,
  transition: {
    open: {
      duration: transitionDuration.enteringScreen,
    },
    close: {
      duration: transitionDuration.leavingScreen,
    },
  },
};

const styles = (theme: Theme) => {
  const clientConfiguration = getClientConfiguration();
  const collapsedNavDrawerWidth = clientConfiguration.ui?.collapsedNavDrawerWidth ?? 0;

  return createStyles({
    root: {
      width: "100%",
      backgroundColor: theme.palette.background.default,
    },
    appFrame: {
      position: "relative",
      display: "flex",
      width: "100%",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
    },
    appBar: {
      left: 0,
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(["left"], {
        easing: theme.transitions.easing.sharp,
        duration: navDrawerConfig.transition.close.duration,
      }),
    },
    appBarShift: {
      left: navDrawerConfig.width,
      transition: theme.transitions.create(["left"], {
        easing: theme.transitions.easing.sharp,
        duration: navDrawerConfig.transition.open.duration,
      }),
    },
    appBarMenuButton: {
      padding: theme.spacing(2),
    },
    appBarMenuCollapseButton: {
      marginLeft: "auto",
    },
    appBarTitle: {
      marginLeft: 10,
      paddingRight: theme.spacing(1), // Needed to prevent clipping end of text in IE11
    },
    appBarContent: {
      display: "flex",
      marginLeft: "auto",
    },
    logo: {
      height: 25,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(1),
    },
    drawerLogo: {
      marginLeft: theme.spacing(1),
    },
    hide: {
      display: "none",
    },
    drawer: {
      position: "fixed",
      flexShrink: 0,
      width: 0,
      borderRight: 0,
      whiteSpace: "nowrap",
      overflowX: "hidden",
      [theme.breakpoints.up("sm")]: {
        width: collapsedNavDrawerWidth,
        borderRight: collapsedNavDrawerWidth > 0 ? `1px solid ${theme.palette.divider}` : 0,
      },
      transition: theme.transitions.create(["width", "border"], {
        easing: theme.transitions.easing.sharp,
        duration: navDrawerConfig.transition.close.duration,
      }),
    },
    drawerOpen: {
      width: navDrawerConfig.width,
      borderRight: `1px solid ${theme.palette.divider}`,
      transition: theme.transitions.create(["width", "border"], {
        easing: theme.transitions.easing.sharp,
        duration: navDrawerConfig.transition.open.duration,
      }),
    },
    drawerBackdrop: {
      // Sourced from https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/Backdrop/Backdrop.js
      position: "fixed",
      right: 0,
      bottom: 0,
      top: 0,
      left: 0,
      zIndex: -1,
      opacity: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      // Remove grey highlight
      WebkitTapHighlightColor: "transparent",
      // Disable scroll capabilities.
      touchAction: "none",
      [theme.breakpoints.up("sm")]: {
        left: collapsedNavDrawerWidth,
      },
      transition: theme.transitions.create(["left", "opacity"], {
        easing: theme.transitions.easing.sharp,
        duration: navDrawerConfig.transition.close.duration,
      }),
    },
    drawerOpenBackdrop: {
      left: navDrawerConfig.width + 1,
      zIndex: theme.zIndex.drawer + 2,
      opacity: 1,
      transition: theme.transitions.create(["left", "opacity"], {
        easing: theme.transitions.easing.sharp,
        duration: navDrawerConfig.transition.open.duration,
      }),
    },
    drawerToolbar: {
      display: "flex",
      alignItems: "center",
      height: layoutContentMarginTop.mobile,
      padding: `0 ${theme.spacing(1)}px`,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.getContrastText(theme.palette.primary.main),
      ...theme.mixins.toolbar,
      [theme.breakpoints.up("sm")]: {
        height: layoutContentMarginTop.nonMobile,
      },
    },
    content: {
      position: "absolute",
      left: 0,
      width: "100%",
      height: `calc(100% - ${layoutContentMarginTop.mobile}px)`,
      marginTop: layoutContentMarginTop.mobile,
      [theme.breakpoints.up("sm")]: {
        left: collapsedNavDrawerWidth,
        width: `calc(100% - ${collapsedNavDrawerWidth}px)`,
        height: `calc(100% - ${layoutContentMarginTop.nonMobile}px)`,
        marginTop: layoutContentMarginTop.nonMobile,
      },
      transition: theme.transitions.create(["left"], {
        easing: theme.transitions.easing.sharp,
        duration: navDrawerConfig.transition.close.duration,
      }),
      "& .app-frame__header, & .app-frame__sub-header": {
        [theme.breakpoints.up("sm")]: {
          width: `calc(100% - ${collapsedNavDrawerWidth + 1}px)`,
        },
      },
      "& .app-frame__title": {
        left: -56,
        paddingLeft: 56,
        [theme.breakpoints.up("sm")]: {
          left: -121 - collapsedNavDrawerWidth,
          paddingLeft: 120 + collapsedNavDrawerWidth,
        },
      },
      "& .app-frame__content-width-constrained": {
        [theme.breakpoints.up("lg")]: {
          marginLeft: -collapsedNavDrawerWidth,
        },
      },
    },
    contentWithNavDrawerShift: {
      left: navDrawerConfig.width,
      transition: theme.transitions.create(["left"], {
        easing: theme.transitions.easing.sharp,
        duration: navDrawerConfig.transition.open.duration,
      }),
    },
    contentWithoutNavDrawer: {
      left: 0,
      width: "100%",
      "& .app-frame__header, & .app-frame__sub-header": {
        [theme.breakpoints.up("sm")]: {
          width: "100%",
        },
      },
      "& .app-frame__title": {
        [theme.breakpoints.up("sm")]: {
          left: -121,
          paddingLeft: 120,
        },
      },
      "& .app-frame__content-width-constrained": {
        [theme.breakpoints.up("lg")]: {
          marginLeft: 0,
        },
      },
    },
    username: {
      marginRight: "16px",
      color: theme.palette.getContrastText(theme.palette.primary.main),
    },
    snackbarNotificationsContainerRoot: {
      [theme.breakpoints.up("sm")]: {
        margin: `0 ${-theme.spacing(1) * 2}px`,
      },
    },
    snackbarNotificationsRoot: {
      position: "relative",
      width: "calc(100% - 16px)",
      "& > div": {
        flexWrap: "inherit",
        paddingLeft: theme.spacing(2),
      },
      "& #client-snackbar": {
        alignItems: "flex-start",
      },
      [theme.breakpoints.up("sm")]: {
        width: "auto",
      },
    },
    snackbarNotificationsRootMaxWidth360: {
      "& #client-snackbar": {
        [theme.breakpoints.up("sm")]: {
          maxWidth: 360,
        },
      },
    },
    snackbarNotificationsBottomLeft: {
      [theme.breakpoints.up("sm")]: {
        left: theme.spacing(2),
      },
    },
    snackbarNotificationsBottomRight: {
      [theme.breakpoints.up("sm")]: {
        right: theme.spacing(2),
      },
    },
  });
};

interface InnerAppFrameStateProps {
  appState: AppState;
  oidcState: OidcState;
}

interface InnerAppFrameDispatchProps {
  appActions: AppActions;
}

export interface InnerAppFrameOwnProps extends RouteComponentProps<any> {
  appName: string;
  shortAppName: string;
  appLogo: { src: string; alt: string };
  appBarMenuOptions?: AppBarMenuOptions;
  drawerMenuOptions?: DrawerMenuOptions;
  userMenuOptions?: UserMenuOptions;
  snackbarNotificationsOptions?: {
    alignment?: "left" | "right";
    maxWidth?: 360;
    maxNotifications?: number;
    autoHideDurationMilliseconds?: number;
  };
  notifications?: JSX.Element;
  children: Array<JSX.Element | null>;
  userSignInHandler?: <T>(accessToken: string) => Promise<UserSignInResultDto<T>>;
  onUserSignedInChange?: (userSignedIn: boolean) => void;
}

type InnerAppFrameProps = InnerAppFrameStateProps &
  InnerAppFrameDispatchProps &
  InnerAppFrameOwnProps &
  WithStyles<typeof styles> &
  WithTheme;

interface InnerAppFrameState {
  userSignedIn?: boolean;
  userSignOutPending: boolean;
  drawerMenuOpen: boolean;
}

export const InnerAppFrameComponent = class extends React.Component<InnerAppFrameProps, InnerAppFrameState> {
  constructor(props: any) {
    super(props);

    this.state = {
      userSignOutPending: false,
      drawerMenuOpen: false,
    };
  }

  public componentDidMount() {
    const { userSignInHandler } = this.props;
    // If no User Sign-In Handler has been specified, then user will always be signed out when using the application...
    if (!userSignInHandler) {
      setUserSignedOut();
    }

    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  public render() {
    const {
      appName,
      shortAppName,
      appLogo,
      appBarMenuOptions,
      drawerMenuOptions,
      userMenuOptions,
      snackbarNotificationsOptions,
      notifications,
      children,
      userSignInHandler,
      appState,
      appActions,
      oidcState,
      location,
      classes,
      history,
      theme,
    } = this.props;
    const { snackbarMessage, snackbarNotifications } = appState;
    const { user } = oidcState;
    const { drawerMenuOpen } = this.state;

    const userSignInPending = userSignInHandler && this.isAuthSignInPage(location);
    const userSignedIn = userSignInHandler && (user ? !user.expired : false);
    const userFullName = userSignInHandler && (user ? getUserFullName(user) : undefined);
    const appMenuButtonOverrideEnabled = this.isAppBarMenuButtonOverrideEnabled(location);
    // If the App Menu Button Override shall be used, then the Drawer Menu must be hidden
    const drawerMenuEnabled = !appMenuButtonOverrideEnabled && this.isDrawerMenuEnabled(location, userSignedIn);

    const windowWidth = window.innerWidth;
    const isWindowSmOrLess = theme ? windowWidth <= theme.breakpoints.values.sm : false;
    const isMobile = isClientMobile();

    const pageTitle = this.getPageTitle(isWindowSmOrLess ? shortAppName : appName, location.pathname);
    document.title = pageTitle.headTitle;

    return (
      <SnackbarProvider
        TransitionComponent={Grow as any}
        maxSnack={snackbarNotificationsOptions?.maxNotifications}
        autoHideDuration={snackbarNotificationsOptions?.autoHideDurationMilliseconds}
        anchorOrigin={{
          horizontal: snackbarNotificationsOptions?.alignment || "right",
          vertical: "bottom",
        }}
        iconVariant={snackbarNotificationIconVariant}
        dense={isMobile}
        classes={{
          containerRoot: classes.snackbarNotificationsContainerRoot,
          root: classNames(
            classes.snackbarNotificationsRoot,
            snackbarNotificationsOptions?.maxWidth === 360 ? classes.snackbarNotificationsRootMaxWidth360 : undefined,
          ),
          anchorOriginBottomLeft: classes.snackbarNotificationsBottomLeft,
          anchorOriginBottomRight: classes.snackbarNotificationsBottomRight,
        }}
      >
        <div className={classes.root}>
          <div id="app-frame" className={classes.appFrame} style={{ height: appState.appFrame.height }}>
            <AppBar
              id="app-frame_header"
              position="fixed"
              className={classNames(classes.appBar, drawerMenuOpen && classes.appBarShift)}
            >
              <Toolbar disableGutters={!drawerMenuOpen}>
                {appMenuButtonOverrideEnabled ? (
                  <>
                    <IconButton
                      color="inherit"
                      aria-label="Toggle Drawer"
                      className={classes.appBarMenuButton}
                      onClick={appBarMenuOptions!.buttonOverride!.onClick}
                    >
                      <MenuIcon />
                    </IconButton>
                    <Link to={"/"} title="Go to Home Page">
                      <img className={classes.logo} src={appLogo.src} alt={appLogo.alt} />
                    </Link>
                  </>
                ) : drawerMenuEnabled ? (
                  <IconButton
                    color="inherit"
                    aria-label={`${drawerMenuOpen ? "Close" : "Open"} Drawer`}
                    className={classNames(classes.appBarMenuButton, drawerMenuOpen && classes.hide)}
                    onClick={this.handleDrawerMenuOpen}
                  >
                    <MenuIcon />
                  </IconButton>
                ) : (
                  <Link to={"/"} title="Go to Home Page">
                    <img className={classes.logo} src={appLogo.src} alt={appLogo.alt} />
                  </Link>
                )}
                <Typography className={classes.appBarTitle} variant="h6" color={"inherit"} noWrap={true}>
                  {pageTitle.appTitle}
                </Typography>
                <div className={classes.appBarContent}>
                  {notifications || null}
                  {userSignInPending ? (
                    <Typography id="username" variant="body1" className={classes.username}>
                      Signing in...
                    </Typography>
                  ) : (
                    <UserMenu
                      options={userMenuOptions}
                      userSignedIn={userSignedIn}
                      userFullName={userFullName}
                      history={history}
                      className={classes.username}
                      onSignOutRequest={this.handleSignOutRequest}
                    />
                  )}
                </div>
              </Toolbar>
            </AppBar>
            {drawerMenuEnabled && (
              <Drawer
                id="app-frame_drawer"
                variant="permanent"
                classes={{ paper: classNames(classes.drawer, drawerMenuOpen && classes.drawerOpen) }}
                open={drawerMenuOpen}
              >
                <div className={classes.drawerToolbar}>
                  <img className={classNames(classes.logo, classes.drawerLogo)} src={appLogo.src} alt={appLogo.alt} />
                  <IconButton
                    color="inherit"
                    aria-label="Close Drawer"
                    className={classes.appBarMenuCollapseButton}
                    onClick={this.handleDrawerMenuClose}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </div>
                {drawerMenuOptions ? (
                  <DrawerMenu options={drawerMenuOptions} currentLocation={location.pathname} />
                ) : null}
              </Drawer>
            )}
            <div
              id="app-frame_content"
              className={classNames(
                classes.content,
                !drawerMenuEnabled && classes.contentWithoutNavDrawer,
                drawerMenuOpen && classes.contentWithNavDrawerShift,
              )}
              style={userSignedIn ? undefined : { paddingLeft: 0 }}
            >
              <Switch>
                {userSignInHandler
                  ? children.concat(
                      <SignInRoute
                        key="/auth/signin"
                        path="/auth/signin"
                        render={routeProps => <SignInHandler onSignInError={this.handleSignInError} {...routeProps} />}
                      />,
                      <SignInRoute
                        key="/auth/signin-oidc"
                        path="/auth/signin-oidc"
                        render={routeProps => (
                          <SignInCallbackHandler
                            onSignInSuccess={this.handleSignInCallbackSuccess}
                            onSignInError={this.handleSignInCallbackError}
                            {...routeProps}
                          />
                        )}
                      />,
                      <SignInRoute
                        key="/auth/signout-oidc"
                        path="/auth/signout-oidc"
                        render={routeProps => (
                          <SignOutCallbackHandler
                            onSignOutSuccess={this.handleSignOutCallbackSuccess}
                            onSignOutError={this.handleSignOutCallbackError}
                            {...routeProps}
                          />
                        )}
                      />,
                      <Route
                        key="/auth/signout-oidc"
                        path="/auth/signout-oidc"
                        render={routeProps => (
                          <SignOutCallbackHandler
                            onSignOutSuccess={this.handleSignOutCallbackSuccess}
                            onSignOutError={this.handleSignOutCallbackError}
                            {...routeProps}
                          />
                        )}
                      />,
                      <Route
                        key="/auth/signed-out"
                        path="/auth/signed-out"
                        render={routeProps => <SignedOutPage onSignInError={this.handleSignInError} {...routeProps} />}
                      />,
                      <Route key="/auth/not-authorized" path="/auth/not-authorized" component={NotAuthorizedPage} />,
                      <Route key="/not-found" component={NotFoundPage} />,
                    )
                  : children.concat(<Route key="/not-found" component={NotFoundPage} />)}
              </Switch>
            </div>
          </div>
          <div
            className={classNames(classes.drawerBackdrop, drawerMenuOpen && classes.drawerOpenBackdrop)}
            onClick={this.handleDrawerBackdropClick}
          />
          <SnackbarMessage {...snackbarMessage} />
          <SnackbarNotifications
            notifications={snackbarNotifications}
            removeNotification={appActions.removeSnackbarNotification}
          />
        </div>
      </SnackbarProvider>
    );
  }

  public async componentDidUpdate(prevProps: InnerAppFrameProps, prevState: InnerAppFrameState) {
    const { oidcState: prevOidcState, location: prevLocation } = prevProps;
    const { isLoadingUser: prevIsLoadingUser } = prevOidcState;
    const { drawerMenuOptions, userSignInHandler, onUserSignedInChange, oidcState, location, history } = this.props;
    const { isLoadingUser, user } = oidcState;
    const { userSignedIn: prevUserSignedIn } = prevState;
    const { userSignOutPending, userSignedIn, drawerMenuOpen } = this.state;

    if (userSignInHandler) {
      const nextUserSignedIn = user ? !user.expired : false;
      // If the user has just become loaded (i.e. after refreshing the page)...
      if (userSignedIn === undefined && prevIsLoadingUser && !isLoadingUser) {
        if (nextUserSignedIn) {
          setUserSignedIn();
        } else {
          setUserSignedOut();
        }
        this.setState({ ...this.state, userSignedIn: nextUserSignedIn });
      }
      // Otherwise, if the user has not just explicitly requested sign-out, but has nonetheless ended up being
      // signed out (and ignore any false positives from the sign-in process)...
      else if (!userSignOutPending && userSignedIn && !nextUserSignedIn && !this.isAuthSignInPage(location)) {
        setUserSignedOut();
        this.setState({ ...this.state, userSignedIn: nextUserSignedIn }, () => {
          history.push("/auth/signed-out");
        });
      }

      // If subscribed for such notifications, and the user has just resolved as signed in after loading the user
      // (i.e. prevUserSignedIn == null), or the user's signed in state has changed while the user has been loaded,
      // emit an event...
      if (
        onUserSignedInChange &&
        userSignedIn != null &&
        ((prevUserSignedIn == null && userSignedIn) || (prevUserSignedIn != null && prevUserSignedIn !== userSignedIn))
      ) {
        // If the user has just signed in, additionally emit whether the user is authorized
        onUserSignedInChange(userSignedIn);
      }
    }

    // If Nav Drawer is to be collapsed upon route change, and the Nav Drawer is open and the path just changed...
    if (
      drawerMenuOptions &&
      drawerMenuOptions.collapseOnRouteChange === true &&
      drawerMenuOpen &&
      prevLocation.pathname !== location.pathname
    ) {
      this.setState({ ...this.state, drawerMenuOpen: false });
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  public handleResize = (event?: UIEvent) => {
    const { appActions, theme } = this.props;

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const appFrameHeight = windowHeight;
    const appFrameContentHeight =
      appFrameHeight -
      (windowWidth < theme.breakpoints.values.sm ? layoutContentMarginTop.mobile : layoutContentMarginTop.nonMobile);

    appActions.updateHeight(appFrameHeight, appFrameContentHeight);
  };

  public handleDrawerMenuOpen = () => {
    this.setState({ ...this.state, drawerMenuOpen: true });
  };

  public handleDrawerMenuClose = () => {
    this.setState({ ...this.state, drawerMenuOpen: false });
  };

  public handleDrawerBackdropClick = () => {
    this.setState({ ...this.state, drawerMenuOpen: false });
  };

  public isAppBarMenuButtonOverrideEnabled = (location: Location<any>) => {
    const { appBarMenuOptions } = this.props;
    // If App Bar Menu or Button Override is not being used...
    if (!appBarMenuOptions || !appBarMenuOptions.buttonOverride) {
      return false;
    }
    // If there are no specified rules for controlling the enabled state of the Override...
    if (!appBarMenuOptions.buttonOverride.enabledRules) {
      return true;
    }

    const { urlRegex } = appBarMenuOptions.buttonOverride.enabledRules;

    if (urlRegex && !urlRegex.test(location.pathname)) {
      return false;
    }

    return true;
  };

  public isDrawerMenuEnabled = (location: Location<any>, userSignedIn?: boolean) => {
    const { drawerMenuOptions } = this.props;
    // If Drawer Menu is not being used...
    if (!drawerMenuOptions || !drawerMenuOptions.items.length) {
      return false;
    }
    // If there are no specified rules for controlling the enabled state of the Drawer Menu...
    if (!drawerMenuOptions.enabledRules) {
      return true;
    }

    const { urlRegex, authorizationPolicies } = drawerMenuOptions.enabledRules;

    if (urlRegex && !urlRegex.test(location.pathname)) {
      return false;
    }

    if (authorizationPolicies && !(userSignedIn && isUserAuthorized(authorizationPolicies))) {
      return false;
    }

    return true;
  };

  public handleSignInError = (error: Error) => {
    const { appActions } = this.props;

    console.error(error);

    const errorMessage =
      "Could not connect to the Authentication Service to sign-in. Please ensure you are connected to the " +
      "network, then try again.";

    appActions.postSnackbarMessage(errorMessage, { variant: "error" });
  };

  public handleSignInCallbackSuccess = async (signInResponse: SigninResponse) => {
    const { appActions, userSignInHandler, history } = this.props;

    const result = await userSignInHandler!(signInResponse.access_token);
    // If the user successfully signed in...
    if (result.signedIn) {
      setUserSignedIn(signInResponse.profile.id, result);
      const theme = result.preferences && (result.preferences as any).theme;
      // Set a user-specified theme, if applicable...
      if (theme) {
        appActions.updateTheme(theme);
      }
      this.setState({ ...this.state, userSignedIn: true }, () => {
        // Navigate to the route the user was originally attempting to access
        history.push(signInResponse.state.path);
      });
    }
    // Otherwise, navigate to the "Not Authorized" page...
    else {
      history.push("/auth/not-authorized");
    }
  };

  public handleSignInCallbackError = (error: Error) => {
    const { appActions } = this.props;

    const errorMessage = "An error occurred upon attempting to sign you in. Please reload the page to try again.";

    appActions.postSnackbarMessage(errorMessage, { variant: "error" });
  };

  public handleSignOutError = (error: Error) => {
    const { appActions } = this.props;

    console.error(error);

    const errorMessage =
      "Could not connect to the Authentication Service to sign-out. Please ensure you are connected to the " +
      "network, then try again.";

    appActions.postSnackbarMessage(errorMessage, { variant: "error" });
  };

  public handleSignOutRequest = () => {
    this.setState({ ...this.state, userSignOutPending: true }, () => {
      getUserManager().signoutRedirect();
    });
  };

  public handleSignOutCallbackSuccess = async (signOutResponse: SignoutResponse) => {
    const { history } = this.props;

    setUserSignedOut();

    this.setState({ ...this.state, userSignOutPending: false, userSignedIn: false }, () => {
      history.push("/auth/signed-out");
    });
  };

  public handleSignOutCallbackError = (error: Error) => {
    const { appActions } = this.props;

    const errorMessage = "An error occurred upon attempting to sign you out. Please reload the page to try again.";

    appActions.postSnackbarMessage(errorMessage, { variant: "error" });

    this.setState({ ...this.state, userSignOutPending: false });
  };

  public isAuthSignInPage = (location: Location<any>) => location.pathname.indexOf("/auth/signin") !== -1;

  public getPageTitle = (appName: string, currentLocation: string) => {
    const pageTitle = {
      headTitle: appName,
      appTitle: appName,
    };
    if (currentLocation.toLowerCase().indexOf("/admin") !== -1) {
      pageTitle.headTitle = `Administration - ${appName}`;
      pageTitle.appTitle = "Administration";
    }
    return pageTitle;
  };
};

export const InnerAppFrame = connect<
  InnerAppFrameStateProps,
  InnerAppFrameDispatchProps,
  InnerAppFrameOwnProps,
  BaseApplicationState
>(
  state => {
    return {
      appState: state.app,
      oidcState: state.oidc,
    };
  },
  dispatch => {
    return {
      appActions: bindActionCreators(baseActionCreators.app, dispatch),
    };
  },
)(withStyles(styles, { withTheme: true })(InnerAppFrameComponent));
